import _LessonAnswerPollQuery from "../__generated__/LessonAnswerPollQuery.graphql";
import _LessonAnswerAnswerMutation from "../__generated__/LessonAnswerAnswerMutation.graphql";
import _LessonAnswerFinishMutation from "../__generated__/LessonAnswerFinishMutation.graphql";
import _LessonAnswerStartMutation from "../__generated__/LessonAnswerStartMutation.graphql";
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuery, graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { Loader2, Mic, Pause, Play, RotateCcw } from 'lucide-react';
import { AudioBridge } from '../audio/bridge';
import { keyGesture } from '../audio/gesture.ts';
import { En } from '../i18n/En';
import { msg, t, type Message } from '../i18n/t';
import { TIMEOUT_MS, answerMode, awaitingTranscript, init, summary, type Effect, type Event, type SentAnswer, type State } from '../lessons/answerMode.ts';
import { Holds, type Ready } from '../lessons/holds.ts';
import { localDay } from '../lessons/localDay.ts';
import { addInterval, finishRetryDelayMs, heardFraction, isComplete, nextSpeed, seekBack, type Interval, type Speed } from '../lessons/player.ts';
import { AttemptRecorder, micErrorCopy, pcmBase64, type RecorderEvent } from './sayItBack.ts';
import type { LessonAnswerAnswerMutation } from '../__generated__/LessonAnswerAnswerMutation.graphql';
import type { LessonAnswerFinishMutation } from '../__generated__/LessonAnswerFinishMutation.graphql';
import type { LessonAnswerPollQuery as LessonAnswerPollQueryType } from '../__generated__/LessonAnswerPollQuery.graphql';
import type { LessonAnswerStartMutation } from '../__generated__/LessonAnswerStartMutation.graphql';
const StartPlay = (_LessonAnswerStartMutation.hash && _LessonAnswerStartMutation.hash !== "60f0a05c891d87bd29768dd18301dc8c" && console.error("The definition of 'LessonAnswerStartMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonAnswerStartMutation);
const FinishPlay = (_LessonAnswerFinishMutation.hash && _LessonAnswerFinishMutation.hash !== "062c08918bd999a27d401ce23a991f6d" && console.error("The definition of 'LessonAnswerFinishMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonAnswerFinishMutation);
const AnswerCue = (_LessonAnswerAnswerMutation.hash && _LessonAnswerAnswerMutation.hash !== "8975f96048e9f199700dd4800fe04a3c" && console.error("The definition of 'LessonAnswerAnswerMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonAnswerAnswerMutation);
const PollQuery = (_LessonAnswerPollQuery.hash && _LessonAnswerPollQuery.hash !== "6fb3a3714373dc0fad35a3c6342d1fde" && console.error("The definition of 'LessonAnswerPollQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonAnswerPollQuery);

/** Transcripts are fetched this often while one is missing (`awaitingTranscript` says for how long). */
const POLL_MS = 2_000;
const MAX_STEP_MS = 1500;
export type AnswerCue = {
  atMs: number;
  text: string;
};
export type DayMedian = {
  localDay: string;
  medianMs?: number | null | undefined;
  answered: number;
  asked: number;
};
const seconds = (ms: number) => (ms / 1000).toFixed(1);
function dayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y as number, (m as number) - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Answer mode (lesson library PRD §4.4). The recording plays, stops where each
 * question ends, and goes on when the learner lets go of the button or six
 * seconds pass. The speaker's own answer in the recording is the feedback, so
 * nothing here says right or wrong, and nothing waits on a model: the clip is
 * handed to the server after the recording has resumed, and its words are
 * read back later.
 *
 * Remounted for each play, so "Play again" is a new play with nothing left
 * over from the last.
 */
export function LessonAnswer(props: {
  lessonId: string;
  fileUrl: string;
  durationMs: number | null | undefined;
  cues: readonly AnswerCue[];
  medians: readonly DayMedian[];
}) {
  const [run, setRun] = useState(0);
  return <Run key={run} {...props} again={() => setRun(n => n + 1)} />;
}
function Run({
  lessonId,
  fileUrl,
  durationMs: knownDuration,
  cues,
  medians,
  again
}: Parameters<typeof LessonAnswer>[0] & {
  again: () => void;
}) {
  const environment = useRelayEnvironment();
  const [startPlay] = useMutation<LessonAnswerStartMutation>(StartPlay);
  const [finishPlay] = useMutation<LessonAnswerFinishMutation>(FinishPlay);
  const [answerCue] = useMutation<LessonAnswerAnswerMutation>(AnswerCue);
  const audio = useRef<HTMLAudioElement>(null);
  const machine = useRef<State>(init(cues.map(c => c.atMs)));
  const [state, setState] = useState<State>(machine.current);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1.0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<Message | null>(null);
  const [sent, setSent] = useState<SentAnswer[]>([]);
  const [playId, setPlayId] = useState<string | null>(null);
  const playIdPromise = useRef<Promise<string> | null>(null);
  const recorder = useRef<AttemptRecorder | null>(null);
  // A hold's two halves arrive in either order (the reducer's release and the
  // recorder's clip), and the recorder may still be draining one clip when the
  // next press comes, so they are paired per cue.
  const holds = useRef(new Holds());
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishRef = useRef<() => void>(() => {});
  const dispatchRef = useRef<(e: Event) => void>(() => {});
  const heard = useRef<Interval[]>([]);
  const last = useRef<number | null>(null);
  const finished = useRef(false);
  const finishFailures = useRef(0);
  const durationMs = knownDuration ?? duration;
  const finish = useCallback(() => {
    if (finished.current || !playIdPromise.current) return;
    finished.current = true;
    // A play that never started has nothing to finish.
    void playIdPromise.current.then(id => finishPlay({
      variables: {
        input: {
          lessonPlayId: id
        }
      },
      onError: () => {
        // `finished` stays set until the delay passes, so the 90 % check on
        // the next frame does not ask again; the retry is made here, since
        // after the end nothing else would call `finish`.
        const delay = finishRetryDelayMs(++finishFailures.current);
        if (delay === null) return;
        finishTimer.current = setTimeout(() => {
          finished.current = false;
          finishRef.current();
        }, delay);
      }
    })).catch(() => {});
  }, [finishPlay]);
  finishRef.current = finish;
  const send = useCallback((info: {
    cueIndex: number;
    responseMs: number | null;
  }, pcm: Int16Array | null) => {
    const clipped = pcm !== null;
    const responseMs = clipped ? info.responseMs : null;
    const at = Date.now();
    const mark = (patch: Partial<SentAnswer>) => setSent(all => all.map(a => a.cueIndex === info.cueIndex && a.at === at ? {
      ...a,
      ...patch
    } : a));
    setSent(s => [...s, {
      cueIndex: info.cueIndex,
      responseMs,
      clipped,
      transcript: null,
      at,
      failed: false
    }]);
    // Best effort: the story has gone on, and a missed answer is not worth
    // interrupting it for. Only this answer stops being waited for, and its
    // row says so; that includes a play that never started, whose promise
    // is gone or rejected.
    const play = playIdPromise.current;
    if (!play) {
      mark({
        failed: true
      });
      return;
    }
    play.then(id => answerCue({
      variables: {
        input: {
          lessonPlayId: id,
          cueIndex: info.cueIndex,
          responseMs,
          pcmBase64: pcm ? pcmBase64(pcm) : null
        }
      },
      onError: () => mark({
        failed: true
      })
    })).catch(() => mark({
      failed: true
    }));
  }, [answerCue]);
  const flush = useCallback((ready: Ready | null) => {
    if (ready) send(ready.info, ready.pcm);
  }, [send]);

  // A refused `play()` (the browser's autoplay rules, a decode error) leaves
  // the element paused while the machine says playing, so it is told. An
  // AbortError is a pause that came first, which the machine already knows.
  const playIt = useCallback((el: HTMLAudioElement | null) => {
    el?.play().catch((err: unknown) => {
      if ((err as {
        name?: string;
      } | null)?.name !== 'AbortError') dispatchRef.current({
        kind: 'pause'
      });
    });
  }, []);
  const perform = useCallback((e: Effect) => {
    const el = audio.current;
    switch (e.kind) {
      case 'pause':
        el?.pause();
        break;
      case 'resume':
        playIt(el);
        break;
      case 'startClip':
        if (holds.current.begin(e.cueIndex)) recorder.current?.press();
        break;
      case 'sendAnswer':
        if (!e.clip) send(e, null);else flush(holds.current.release(e));
        break;
    }
  }, [send, flush, playIt]);
  const dispatch = useCallback((event: Event) => {
    const r = answerMode(machine.current, event);
    machine.current = r.state;
    setState(r.state);
    for (const e of r.effects) perform(e);
  }, [perform]);
  dispatchRef.current = dispatch;
  const onRecorder = useCallback((e: RecorderEvent) => {
    switch (e.kind) {
      case 'clip':
        // Only the answer to this hold matters: nothing waits for it, and a
        // second press is possible at once.
        recorder.current?.settle();
        flush(holds.current.resolve(e.pcm));
        break;
      case 'tooShort':
        // A mis-tap is sent as no answer, as the server would read it.
        flush(holds.current.resolve('none'));
        break;
      case 'micError':
        setError(micErrorCopy(e.error));
        flush(holds.current.resolve('none'));
        break;
      case 'ready':
        // Let go before the microphone opened (a permission prompt takes
        // the pointer): no clip is coming.
        if (!e.held) flush(holds.current.resolve('none'));
        break;
    }
  }, [flush]);

  // One microphone per hold, opened when the learner presses and released
  // when the clip is in (media-channel design §4.5): `AttemptRecorder` is the
  // same single owner say-it-back uses.
  useEffect(() => {
    const r = new AttemptRecorder((onPcm, onEnded) => new AudioBridge(onPcm, onEnded, () => {}), onRecorder);
    recorder.current = r;
    return () => {
      r.dispose();
      recorder.current = null;
    };
  }, [onRecorder]);
  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed]);

  // The cue check: a frame loop reading the element's clock, since
  // `timeupdate` is too coarse to stop at a cue.
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const step = () => {
      const el = audio.current;
      if (el && !el.paused) {
        const ms = el.currentTime * 1000;
        if (last.current !== null && ms - last.current > 0 && ms - last.current < MAX_STEP_MS) {
          heard.current = addInterval(heard.current, last.current, ms);
          if (isComplete(heardFraction(heard.current, durationMs))) finish();
        }
        last.current = ms;
        dispatch({
          kind: 'tick',
          posMs: ms,
          nowMs: performance.now()
        });
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [playing, dispatch, finish, durationMs]);

  // The six-second wait, derived from the state: absent while the tab is
  // hidden, never started while held.
  const stoppedAt = state.kind === 'waiting' ? state.stoppedAt : null;
  useEffect(() => {
    if (stoppedAt === null) return;
    const timer = setTimeout(() => dispatch({
      kind: 'timeout'
    }), Math.max(0, stoppedAt + TIMEOUT_MS - performance.now()));
    return () => clearTimeout(timer);
  }, [stoppedAt, dispatch]);
  useEffect(() => {
    const onVisibility = () => dispatch(document.hidden ? {
      kind: 'hidden'
    } : {
      kind: 'visible',
      nowMs: performance.now()
    });
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [dispatch]);

  // Transcripts land in the background: ask every 2 s while any clip's is
  // missing and under a minute old, then stop quietly. Derived from what was
  // sent, so a later clip never ends the wait for an earlier one.
  const waitingFor = awaitingTranscript(sent, Date.now()).length;
  useEffect(() => {
    if (waitingFor === 0 || !playId) return;
    let live = true;
    const timer = setTimeout(() => {
      const again = () => {
        // A new array even when nothing changed, so this runs again (and
        // `awaitingTranscript` sees the clock) until nothing is waited for.
        if (live) setSent(all => [...all]);
      };
      fetchQuery<LessonAnswerPollQueryType>(environment, PollQuery, {
        id: playId
      }, {
        fetchPolicy: 'network-only'
      }).toPromise().then(r => {
        if (!live) return;
        const got = new Map((r?.node?.answers ?? []).map(a => [a.cueIndex, a.transcript]));
        setSent(all => all.map(s => ({
          ...s,
          transcript: s.transcript ?? got.get(s.cueIndex) ?? null
        })));
      }).catch(again);
    }, POLL_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [waitingFor, playId, sent, environment]);
  useEffect(() => () => {
    if (finishTimer.current) clearTimeout(finishTimer.current);
  }, []);
  useEffect(() => {
    if (state.kind === 'done') finish();
  }, [state.kind, finish]);
  const toggle = () => {
    const el = audio.current;
    if (!el || machine.current.kind === 'done') return;
    if (!playIdPromise.current) {
      playIdPromise.current = new Promise<string>((resolve, reject) => startPlay({
        variables: {
          input: {
            lessonId,
            localDay: localDay(new Date()),
            mode: 'ANSWER'
          }
        },
        onCompleted: r => {
          setPlayId(r.startLessonPlay.id);
          resolve(r.startLessonPlay.id);
        },
        onError: err => {
          // Without a play there is nowhere to record answers, so say so and
          // stop rather than let them drop; the next press tries again.
          playIdPromise.current = null;
          setError(msg('lessons-answer-start-failed'));
          dispatch({
            kind: 'pause'
          });
          reject(err);
        }
      }));
      playIdPromise.current.catch(() => {});
      setError(null);
    }
    // While a question waits, play and pause are not the learner's to use.
    const k = machine.current.kind;
    if (k === 'waiting' || k === 'holding') return;
    dispatch({
      kind: k === 'idle' ? 'play' : 'pause'
    });
  };
  const talkDown = () => dispatch({
    kind: 'press',
    nowMs: performance.now()
  });
  const talkUp = () => {
    // Resumes first, then lets the recorder finish the clip.
    dispatch({
      kind: 'release',
      nowMs: performance.now()
    });
    recorder.current?.release();
  };
  const onKey = (kind: 'down' | 'up') => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
    const g = keyGesture(e.key, kind, e.repeat);
    if (g === 'down') talkDown();else if (g === 'up') talkUp();
  };
  const asked = state.next;
  const holding = state.kind === 'holding';
  const waiting = state.kind === 'waiting' && state.stoppedAt !== null;

  // Each question appears once it has been heard, so none is read before it is
  // listened to; its words sit under it as they land, on the end screen too.
  const list = <ul className="flex max-h-36 flex-col gap-2 overflow-y-auto" aria-live="polite">
      {cues.slice(0, asked).map((c, i) => {
      const s = sent.find(x => x.cueIndex === i);
      return <li key={c.atMs} className="text-sm">
            <En>{c.text}</En>
            {s?.transcript ? <p className="text-base-content/70 ps-3">
                <En>{s.transcript}</En>
              </p> : s && awaitingTranscript([s], Date.now()).length > 0 ? <p className="text-base-content/50 ps-3">
                <Loader2 aria-hidden className="me-1 inline size-3 animate-spin" />
                {t('lessons-answer-listening')}
              </p> : s?.failed ? <p className="text-error ps-3">{t('lessons-answer-not-saved')}</p> : s && !s.clipped ? <p className="text-base-content/50 ps-3">{t('lessons-answer-none')}</p> : null}
          </li>;
    })}
    </ul>;
  if (state.kind === 'done') {
    const responses = cues.map((_, i) => sent.find(s => s.cueIndex === i)?.responseMs ?? null);
    const mine = summary(responses);
    const today = localDay(new Date());
    // Earlier days as the server counted them when this screen opened, then
    // this play, the latest, from what it just heard.
    const days = [...medians.filter(m => m.localDay !== today && m.medianMs != null), {
      localDay: today,
      medianMs: mine.medianMs,
      answered: mine.answered,
      asked: mine.asked
    }];
    return <div className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">{t('lessons-answer-done')}</h2>
        <p>{t('lessons-answer-count', {
          answered: mine.answered,
          asked: cues.length
        })}</p>
        {mine.medianMs !== null && <p>{t('lessons-answer-median', {
          seconds: seconds(mine.medianMs)
        })}</p>}
        {days.length > 1 && <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold">{t('lessons-answer-earlier')}</h3>
            <ul className="flex flex-wrap gap-2">
              {days.map((d, i) => <li key={d.localDay} className={`badge badge-lg ${i === days.length - 1 ? 'badge-primary' : 'badge-outline'}`}>
                  {d.medianMs == null ? dayLabel(d.localDay) : t('lessons-answer-day-median', {
              day: dayLabel(d.localDay),
              seconds: seconds(d.medianMs)
            })}
                </li>)}
            </ul>
          </div>}
        {list}
        <button className="btn btn-primary min-h-(--hit-min)" onClick={again}>
          {t('lessons-answer-again')}
        </button>
      </div>;
  }
  return <div className="flex flex-col gap-3">
      <audio ref={audio} src={fileUrl} preload="metadata" onPlay={() => {
      setPlaying(true);
      last.current = (audio.current?.currentTime ?? 0) * 1000;
    }} onPause={() => {
      setPlaying(false);
      // Not only our own pauses: a screen lock, a call or a headphone
      // button pauses the element too, and the machine must not go on
      // saying `playing` (the machine ignores the ones it caused).
      dispatch({
        kind: 'pause'
      });
    }} onSeeking={() => last.current = null} onSeeked={() => last.current = (audio.current?.currentTime ?? 0) * 1000} onLoadedMetadata={() => setDuration((audio.current?.duration ?? 0) * 1000)} onEnded={() => {
      setPlaying(false);
      dispatch({
        kind: 'ended'
      });
    }} />
      <div className="flex items-center gap-3">
        <button className="btn btn-primary btn-circle btn-lg" aria-label={playing ? t('lessons-pause') : t('lessons-play')} onClick={toggle}>
          {playing ? <Pause aria-hidden /> : <Play aria-hidden />}
        </button>
        <button className="btn btn-ghost" aria-label={t('lessons-back-15')} onClick={() => {
        if (audio.current) audio.current.currentTime = seekBack(audio.current.currentTime * 1000) / 1000;
      }}>
          <RotateCcw aria-hidden className="size-5" />
          <span aria-hidden>15</span>
        </button>
        <span className="ms-auto text-sm tabular-nums">
          {t('lessons-answer-progress', {
          n: Math.min(asked, cues.length),
          total: cues.length
        })}
        </span>
        <button className="btn btn-outline btn-sm min-h-(--hit-min)" onClick={() => setSpeed(s => nextSpeed(s))}>
          {t('lessons-speed', {
          speed: speed === 1.0 ? '1.0' : '0.85'
        })}
        </button>
      </div>
      {/* `touch-none` and pointer capture as say-it-back's button: a thumb that
          drifts mid-hold must not turn into a pan that cancels the answer. */}
      <button type="button" className={`btn btn-block btn-lg min-h-(--hit-min) gap-2 touch-none select-none [-webkit-touch-callout:none] ${holding ? 'btn-error' : 'btn-outline'} ${waiting ? 'animate-pulse btn-primary' : ''}`} aria-pressed={holding} onPointerDown={e => {
      // Pointer capture, or a release outside the button never arrives.
      e.currentTarget.setPointerCapture(e.pointerId);
      talkDown();
    }} onPointerUp={talkUp} onPointerCancel={talkUp} onKeyDown={onKey('down')} onKeyUp={onKey('up')}>
        <Mic aria-hidden className="size-5" />
        {holding ? t('lessons-answer-release') : t('lessons-answer-hold')}
      </button>
      <p role="alert" className="text-error min-h-5 text-sm">
        {error && t(error)}
      </p>
      {/* Bounded, so a long story's questions scroll in here and the page,
          with the controls above, never does (PRD §9.7). */}
      {list}
    </div>;
}